<template>
	<div>
		<div class="bg-appearance lang-detect" v-if="targetLang">
			<div class="container-max px-0">
				<div class="d-flex flex-column flex-md-row">
					<div class="d-block text-center align-self-center p-2">{{ textTranslations[targetLang] }}</div>

					<div
						class="d-flex flex-row px-2 pt-md-2 pb-2 m-auto mr-md-0 text-nowrap justify-content-between redirect"
					>
						<a :href="redirectUrl" :class="'btn btn-sm btn-appearance'">
							<i :class="'mr-2 flag-icon flag-icon-' + targetLang" />
							{{ buttonTranslations[targetLang] }}
						</a>
						<a
							href="#"
							@click="refuseRedirect()"
							class="align-self-center justify-content-center d-inline-flex lang-close-button"
						>
							<i class="fa fa-fw fa-times"></i>
						</a>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showPresets" class="bg-appearance shipping-popup">
			<div class="container-max py-3 px-3">
				<div v-if="!isLoading" class="d-flex flex-column align-items-center py-4">
					<p class="d-block py-1 px-2 h4 mb-4 mr-2">{{ $translate("Eros::Template.shippingCountryPresetSelectLabel") }}</p>
					<div class="country-selection">
						<template v-for="filteredShippingCountry in filteredShippingCountries">
							<button class="btn mb-2 btn-sm btn-appearance" :key="filteredShippingCountry.id" @click="setPreset(filteredShippingCountry)">
								<i :class="'mr-2 flag-icon flag-icon-' + filteredShippingCountry.isoCode2.toLowerCase()"></i>
								{{ filteredShippingCountry.currLangName }}
							</button>
						</template>
					</div>
				</div>
				<loading-animation v-else></loading-animation>
			</div>
		</div>
	</div>
</template>

<script>
import { whenConsented } from "../../helper/whenConsented";
import { navigateTo } from "../../services/UrlService";
import { mapState } from "vuex";

export default {
	name: "language-detection",

	data() {
		return {
			redirectUrl: null,
			targetLang: null,
			presetsVisible: false,
			isLoading: false
		};
	},

	props: {
		autoRedirect: Boolean,
		textTranslations: {
			type: Object,
			default: () => {}
		},
		buttonTranslations: {
			type: Object,
			default: () => {}
		},
		languageMap: {
			type: Object,
			default: () => {}
		},
		showDefaultLang: {
			type: Boolean,
			default: false
		}
	},

	computed: {
		browserLanguages() {
			if (!window) {
				return [];
			}

			return [...new Set(window.navigator.languages.map(language => language.split("-")[0]))];
		},

		showPresets() {
			return this.presetsVisible && Object.keys(App.Eros.Config.general.shippingCountryLimitations || {}).length > 0;
		},

		filteredShippingCountries() {
			const limitationKeys = Object.keys(App.Eros.Config.general.shippingCountryLimitations || {});

			return App.Eros.Config.general.visibleShippingCountries.length ? this.localization.shippingCountries.filter(shippingCountry => {
				return App.Eros.Config.general.visibleShippingCountries.includes(shippingCountry.isoCode2) && limitationKeys.includes(shippingCountry.isoCode2);
			}) : this.localization.shippingCountries;
		},

		...mapState({
			localization: (state) => state.localization
		}),
	},

	mounted() {
		if (!App.isShopBuilder) {
			if (!window.localStorage.getItem("hideShippingCountryPreset")) {
				this.presetsVisible = true;
			}
			if (!window.localStorage.getItem("redirectDeactivated")) {
				this.initialize();
			}
		} else {
			this.targetLang = App.defaultLanguage;
		}
	},

	methods: {
		initialize() {
			for (const browserLanguage of this.browserLanguages) {
				const targetLang = this.languageMap[browserLanguage];
				// exclude current language and check if the language has a mapped target language

				if (targetLang !== App.language) {
					this.redirectUrl = this.getLanguageUrl(targetLang);

					if (!this.redirectUrl && this.showDefaultLang) {
						this.redirectUrl = this.getLanguageUrl("other");
					}

					// if the site is not configured in the language
					if (this.redirectUrl) {
						// if a redirect url was found and the auto redirect is enabled, navigate to the redirectUrl
						if (this.autoRedirect) {
							navigateTo(this.redirectUrl);
						} else {
							// this line enables this component rendering
							this.targetLang = targetLang;
						}
						break;
					}
				}
				// if the client is already the wanted language
				else if (targetLang === App.language) {
					break;
				}
			}
		},

		getLanguageUrl(language) {
			return document.querySelector(`link[hreflang="${language}"]`)?.getAttribute("href");
		},

		refuseRedirect() {
			this.targetLang = null;

			this.$nextTick(() => {
				if (typeof window.HeaderScroller !== "undefined") {
					window.HeaderScroller.collectHeaderElementHeights();
					window.HeaderScroller.calculateBodyOffset();
				}
			});

			whenConsented(
				"",
				() => {
					window.localStorage.setItem("redirectDeactivated", true);
				},
				() => {}
			);
		},

		setPreset(filteredShippingCountry) {
			this.isLoading = true;

			if (!window.localStorage.getItem("hideShippingCountryPreset")) {
				window.localStorage.setItem("hideShippingCountryPreset", true);
			}

			const limitation = App.Eros.Config.general.shippingCountryLimitations[filteredShippingCountry.isoCode2];
			const language = limitation.languages[0].toLowerCase();
			const url = document.querySelector(`div#countrySettings a[data-testing="lang-select-${language}"]`)?.getAttribute("href") || "";

			this.$store.dispatch("selectShippingCountry", {
				shippingCountryId: filteredShippingCountry.id,
				openBasketPreview: false,
				url,
				currency: limitation.currencies[0]
			});
		}
	}
};
</script>
